import { useState, useEffect } from 'react'
import { useBrowseStore } from '@/stores/useBrowseStore'
import { useDepartmentSelectionStore } from '@/stores/useDepartmentSelectionStore'
import { BrowseBreadcrumb } from './BrowseBreadcrumb'
import { DivisionGrid } from './DivisionGrid'
import { DepartmentGrid } from './DepartmentGrid'
import { CalendarGrid } from './CalendarGrid'
import { EventGrid } from './EventGrid'
import { BrowsePlansGrid } from './BrowsePlansGrid'
import { DepartmentBulkActionsBar } from './DepartmentBulkActionsBar'
import { ShareDepartmentsModal } from './ShareDepartmentsModal'
import type { BrowsePlansParams } from '@/api/planBrowse'

export function FolderBrowseView() {
  const {
    path,
    selectDivision,
    selectDepartment,
    selectCalendar,
    selectEvent,
    navigateToLevel,
    getCurrentLevel,
  } = useBrowseStore()

  const { selectedDepartments, clearSelection } = useDepartmentSelectionStore()
  const [shareModalOpen, setShareModalOpen] = useState(false)

  const level = getCurrentLevel()

  // Scope sharing selection to the current department grid, including unmounts.
  useEffect(() => {
    clearSelection()
    return clearSelection
  }, [level, path.division?.id, clearSelection])

  // Build filters for the plans grid
  const buildPlansFilters = (): Omit<
    BrowsePlansParams,
    'page' | 'pageSize'
  > | null => {
    if (!path.department) return null

    const filters: Omit<BrowsePlansParams, 'page' | 'pageSize'> = {
      departmentId: path.department.id,
    }

    if (path.calendar) {
      if (path.calendar.noCalendar) {
        filters.noCalendar = true
      } else {
        filters.calendarId = path.calendar.calendarId
        filters.isSeasonal = path.calendar.isSeasonal
      }
    }

    if (path.event) {
      filters.calendarEventId = path.event.eventId
    }

    return filters
  }

  return (
    <div className="flex flex-col gap-4">
      {level !== 'divisions' && (
        <BrowseBreadcrumb
          path={path}
          currentLevel={level}
          onNavigate={navigateToLevel}
        />
      )}

      {level === 'divisions' && <DivisionGrid onSelect={selectDivision} />}

      {level === 'departments' && path.division && (
        <>
          {selectedDepartments.size > 0 && (
            <DepartmentBulkActionsBar
              selectedCount={selectedDepartments.size}
              onClearSelection={clearSelection}
              onShare={() => setShareModalOpen(true)}
            />
          )}
          <DepartmentGrid
            key={path.division.id}
            divisionId={path.division.id}
            onSelect={selectDepartment}
          />
          <ShareDepartmentsModal
            open={shareModalOpen}
            onOpenChange={setShareModalOpen}
            departmentIds={Array.from(selectedDepartments.keys())}
            departmentNames={Array.from(selectedDepartments.values()).map(
              d => d.name
            )}
            onSuccess={clearSelection}
          />
        </>
      )}

      {level === 'calendars' && path.department && (
        <CalendarGrid
          departmentId={path.department.id}
          onSelect={selectCalendar}
        />
      )}

      {level === 'events' &&
        path.department &&
        path.calendar &&
        path.calendar.calendarId && (
          <EventGrid
            departmentId={path.department.id}
            calendarId={path.calendar.calendarId}
            onSelect={selectEvent}
          />
        )}

      {level === 'plans' &&
        (() => {
          const filters = buildPlansFilters()
          return filters ? <BrowsePlansGrid filters={filters} /> : null
        })()}
    </div>
  )
}
